import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Button, Col, Form, Modal, Row, Table } from 'react-bootstrap';
import { filter, find, forEach, get, isEmpty, map } from 'lodash';
import { Bert } from 'meteor/themeteorchef:bert';

import * as atlasOps from '../../../api/Atlas/atlas-operations';
import * as layerOps from '../../../api/Layers/layer-operations';
import * as nodeOps from '../../../api/Tasks/task-node-operations';
import * as taskOps from '../../../api/Tasks/task-operations';
import * as paramOps from '../../../api/Tasks/task-param-operations';
import { SUBTASK_PARAM_TYPES } from '../../../api/Tasks/taskConstants';

import methodPromise from '../../../modules/method-promise-wrapper';
import handleErrorInClient from '../../../modules/handle-error-in-client';
import { emailRegex } from '../../../modules/regex-utils';
import focusedTrainTask from '../../../modules/tasks/focusedTrainTask';

import SubtaskBooleanField from '../../atoms/SubtaskBooleanField';
import SubtaskDateField from '../../atoms/SubtaskDateField';
import SubtaskNumberField from '../../atoms/SubtaskNumberField';
import SubtaskSelectField from '../../atoms/SubtaskSelectField';
import SubtaskTextField from '../../atoms/SubtaskTextField';
import EmptyTableAlert from '../EmptyTableAlert';
import List from '../List';
import SearchInput from '../SearchInput';
import TablePagination from '../TablePagination';
import Validation from '../../containers/Validation';

import Styles from './styles';

const ParamFields = ({ parameters }) => {
  if (isEmpty(parameters)) return '';
  return map(parameters, parameter => {
    if (paramOps.hasOptions(parameter))
      return <SubtaskSelectField parameter={parameter} key={paramOps.getId(parameter)} />;
    switch (paramOps.getType(parameter)) {
      case SUBTASK_PARAM_TYPES.STRING:
        return <SubtaskTextField parameter={parameter} key={paramOps.getId(parameter)} />;
      case SUBTASK_PARAM_TYPES.NUMBER:
        return <SubtaskNumberField parameter={parameter} key={paramOps.getId(parameter)} />;
      case SUBTASK_PARAM_TYPES.BOOLEAN:
        return <SubtaskBooleanField parameter={parameter} key={paramOps.getId(parameter)} />;
      case SUBTASK_PARAM_TYPES.DATE:
        return <SubtaskDateField parameter={parameter} key={paramOps.getId(parameter)} />;
      default:
        return <SubtaskTextField parameter={parameter} key={paramOps.getId(parameter)} />;
    }
  });
};

ParamFields.propTypes = {
  parameters: PropTypes.arrayOf(PropTypes.object).isRequired
};

class FocusedTrainTaskConfiguration extends Component {
  state = {
    currentPage: 1,
    parameters: [],
    searchValue: '',
    showAdvancedOptions: false,
    showModal: false
  };

  componentDidMount() {
    this.getParameters();
  }

  changePage = pageNumber => this.setState({ currentPage: pageNumber });

  changeShowAdvancedOptions = event => this.setState({ showAdvancedOptions: event.target.checked });

  closeModal = () => this.setState({ showModal: false });

  getParameters = async () => {
    const { gmsConnection } = this.props;
    const debugIP = isEmpty(gmsConnection) ? '' : atlasOps.getGMSDirection(gmsConnection);
    const parameters = await methodPromise('tasks.getFocusedTrainParamsList', debugIP, false);
    this.setState({ parameters });
  };

  handleSearch = event => {
    event.persist();
    this.setState({ searchValue: event.target.value.trim() });
  };

  handleSubmit = async form => {
    try {
      const { aois, atlas, entities, gmsConnection, isPublic, layers, selectedLayer } = this.props;
      const { parameters, showAdvancedOptions } = this.state;

      if (isEmpty(aois.features)) throw new Error('Empty areas of interest');
      if (isEmpty(entities.features)) throw new Error('Empty entities');
      if (isEmpty(selectedLayer)) throw new Error('Empty orthophoto');

      const layer = find(layers, ['_id', selectedLayer]);

      const emails = map(form.emailAddress.value.split(/[;,]+/), email => email.trim());
      const emailsForTask = emails.join(' ');

      const name = form.alias.value.trim();
      const existingTasks = await methodPromise('tasks.checkAtlasTaskName', {
        atlas,
        isPublic,
        label: taskOps.getLabel(focusedTrainTask({})),
        name
      });
      let nameForTask = name;
      if (existingTasks > 0) nameForTask += `_${existingTasks}`;

      const taskToRun = focusedTrainTask({
        aois: JSON.stringify(aois),
        emails: emailsForTask,
        entities: JSON.stringify(entities),
        orthophoto: layer,
        owner: atlasOps.getOwner(atlas),
        taskName: nameForTask
      });

      const focusedTrainSubtask = find(taskOps.getNodes(taskToRun), ['id', 'focusedTrain']);
      if (showAdvancedOptions)
        forEach(parameters, parameter => {
          const parameterToUpdate = find(nodeOps.getParamList(focusedTrainSubtask), [
            'id',
            paramOps.getId(parameter)
          ]);
          switch (true) {
            case paramOps.isBoolean(parameter):
              parameterToUpdate.value = get(form, [paramOps.getId(parameter), 'checked'], false);
              break;
            case paramOps.isMultiValue(parameter):
              parameterToUpdate.value = filter(
                map(
                  filter(
                    get(form, [paramOps.getId(parameter), 'options'], []),
                    option => option.selected
                  ),
                  option => option.value
                ),
                value => !isEmpty(value)
              );
              if (paramOps.isRequired(parameter) && isEmpty(parameterToUpdate.value))
                throw new Error(`Empty value in ${paramOps.getLabel(parameter)}`);
              break;
            case paramOps.isNumber(parameter):
            case paramOps.isDate(parameter):
              parameterToUpdate.value = get(form, [paramOps.getId(parameter), 'value'], '');
              if (paramOps.isRequired(parameter) && parameterToUpdate.value === '')
                throw new Error(`Empty value in ${paramOps.getLabel(parameter)}`);
              break;
            default:
              parameterToUpdate.value = get(form, [paramOps.getId(parameter), 'value'], '');
              if (paramOps.isRequired(parameter) && isEmpty(parameterToUpdate.value))
                throw new Error(`Empty value in ${paramOps.getLabel(parameter)}`);
              break;
          }
        });

      await methodPromise('tasks.runTask', {
        authenticate: false,
        debugIP: isEmpty(gmsConnection) ? '' : atlasOps.getGMSDirection(gmsConnection),
        isTasker: false,
        itemForOwner: { id: atlasOps.getId(atlas), collectionName: 'atlas' },
        taskToRun
      });

      this.setState({ showModal: false });
      Bert.alert(`Task launched with alias ${nameForTask}`, 'info');
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  openModal = () => this.setState({ showModal: true });

  render() {
    const {
      isPreview,
      isPublic,
      isEntities,
      itemsPerPage,
      layers,
      selectedLayer,
      updateFlag,
      updateSelectedLayer
    } = this.props;
    const { currentPage, parameters, searchValue, showAdvancedOptions, showModal } = this.state;

    const validationProps = {
      rules: {
        alias: {
          required: true
        },
        emailAddress: {
          required: true,
          multiemails: emailRegex
        }
      },
      messages: {
        alias: {
          required: "What's the task alias?"
        },
        emailAddress: {
          required: 'Need an email address here.',
          multiemails: 'Are all these email addresses correct?'
        }
      },
      submitHandler: form => {
        this.handleSubmit(form);
      }
    };

    const searchRegex = new RegExp(searchValue, 'i');
    const filteredLayers = filter(layers, layer => searchRegex.test(layerOps.getName(layer)));

    const skip = currentPage * itemsPerPage - itemsPerPage;
    const layersToShow = filter(
      filteredLayers,
      (layer, index) => index >= skip && index < skip + itemsPerPage
    );

    const listProps = {
      clickAction: 'select',
      deleteButton: false,
      items: layersToShow,
      loading: false,
      datesColumns: false,
      selectedRow: selectedLayer,
      handleSelectRow: updateSelectedLayer
    };

    return (
      <Styles.AITaskConfigurationContainer>
        <Modal show={showModal} onHide={this.closeModal}>
          <Modal.Header closeButton>
            <Modal.Title>Introduce your data</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Validation {...validationProps}>
              <Form onSubmit={event => event.preventDefault()}>
                <Form.Row>
                  <Form.Group as={Col} xs={12}>
                    <Form.Label>Alias</Form.Label>
                    <Form.Control type="text" name="alias" defaultValue="" />
                  </Form.Group>
                  <Form.Group as={Col} xs={12}>
                    <Form.Label>Email Addresses</Form.Label>
                    <Form.Control type="text" name="emailAddress" defaultValue="" />
                  </Form.Group>
                </Form.Row>
                <Form.Row>
                  <Form.Check
                    type="switch"
                    id="advanced-options"
                    label="Advanced Options"
                    checked={showAdvancedOptions}
                    onChange={this.changeShowAdvancedOptions}
                  />
                </Form.Row>
                {showAdvancedOptions && <ParamFields parameters={parameters} />}
                <Form.Row>
                  <Col xs={{ span: 4, offset: 4 }} className="pt-3">
                    <Button block type="submit" variant="success">
                      Run Detection
                    </Button>
                  </Col>
                </Form.Row>
              </Form>
            </Validation>
          </Modal.Body>
        </Modal>
        <Form>
          <Form.Row className="row-cols-3 m-0">
            <Col xs="2" />
            <Form.Check
              as={Form.Col}
              className="m-0"
              inline
              type="radio"
              id="isEntities"
              label="Features"
              checked={isEntities}
              onChange={() => updateFlag(true)}
            />
            <Form.Check
              as={Form.Col}
              className="m-0"
              inline
              type="radio"
              id="isAOIS"
              label="AI Areas"
              checked={!isEntities}
              onChange={() => updateFlag(false)}
            />
          </Form.Row>
        </Form>
        <Row>
          <Col>
            <Table responsive hover size="lg">
              <thead>
                <tr>
                  <th className="p-0">
                    <Form.Group as={Row} className="m-0 py-1">
                      <Form.Label column sm="4" className="px-0">
                        Orto Photo
                      </Form.Label>
                      <Col sm="8">
                        <SearchInput onKeyUp={this.handleSearch} />
                      </Col>
                    </Form.Group>
                  </th>
                </tr>
              </thead>
              {!isEmpty(layers) ? (
                <List {...listProps} size="lg" />
              ) : (
                <EmptyTableAlert message="No WMS Laters yet!" numberOfColumns={2} />
              )}
            </Table>
            <TablePagination
              currentPage={currentPage}
              isPaginationOn
              itemsPerPage={itemsPerPage}
              totalCount={filteredLayers.length}
              changePage={this.changePage}
            />
          </Col>
        </Row>
        <Row className="mb-3 pb-3">
          <Col
            xs={{ span: 4, offset: 4 }}
            sm={{ span: isPublic ? 4 : 6, offset: isPublic ? 4 : 3 }}
            md={{ span: isPublic ? 8 : 10, offset: isPublic ? 2 : 1 }}
            lg={{ span: isPublic ? 6 : 8, offset: isPublic ? 3 : 2 }}
            xl={{ span: 6, offset: 3 }}
          >
            <Button block disabled={isPreview} variant="primary" onClick={this.openModal}>
              Configuration
            </Button>
          </Col>
        </Row>
      </Styles.AITaskConfigurationContainer>
    );
  }
}

FocusedTrainTaskConfiguration.propTypes = {
  aiInterestArea: PropTypes.object.isRequired,
  atlas: PropTypes.object.isRequired,
  aois: PropTypes.object.isRequired,
  entities: PropTypes.object.isRequired,
  gmsConnection: PropTypes.object.isRequired,
  isEntities: PropTypes.bool.isRequired,
  isPreview: PropTypes.bool.isRequired,
  isPublic: PropTypes.bool.isRequired,
  itemsPerPage: PropTypes.number.isRequired,
  layers: PropTypes.array.isRequired,
  selectedLayer: PropTypes.string.isRequired,
  selectedTaskEntities: PropTypes.array.isRequired,
  updateFlag: PropTypes.func.isRequired,
  updateSelectedLayer: PropTypes.func.isRequired
};

export default FocusedTrainTaskConfiguration;
